/**
 * State chart and related functions go here to keep the Tooltip component
 * from getting too hairy.
 */

import { StateChart, StateContext, Transition } from './types';
import {
  clearContextId,
  clearLeavingVisibleTimer,
  clearRestTimer,
  startLeavingVisibleTimer,
  startRestTimer,
} from './utils';

export const MOUSE_REST_TIMEOUT = 100;
export const LEAVE_TIMEOUT = 500;

export enum TooltipStates {
  /** Nothing goin' on */
  IDLE = 'IDLE',
  /** We're considering showing the tooltip, but we're gonna wait a sec */
  FOCUSED = 'FOCUSED',
  /** It's on! */
  VISIBLE = 'VISIBLE',
  /** Focus has left, but we want to keep it visible for a sec */
  LEAVING_VISIBLE = 'LEAVING_VISIBLE',
  /**
   * The user clicked the tool, so we want to hide the thing, we can't just use
   * IDLE because we need to ignore mousemove, etc.
   */
  DISMISSED = 'DISMISSED',
}

export enum TooltipEvent {
  BLUR = 'BLUR',
  FOCUS = 'FOCUS',
  GLOBAL_MOUSE_MOVE = 'GLOBAL_MOUSE_MOVE',
  MOUSE_DOWN = 'MOUSE_DOWN',
  MOUSE_ENTER = 'MOUSE_ENTER',
  MOUSE_LEAVE = 'MOUSE_LEAVE',
  MOUSE_MOVE = 'MOUSE_MOVE',
  REST = 'REST',
  SELECT_WITH_KEYBOARD = 'SELECT_WITH_KEYBOARD',
  TIME_COMPLETE = 'TIME_COMPLETE',
}

/**
 * Chart context allows us to persist some data around, in Tooltip all we use
 * is the id of the current tooltip being interacted with.
 */
export let context: StateContext = { id: null };

const chart: StateChart = {
  initial: TooltipStates.IDLE,
  states: {
    [TooltipStates.IDLE]: {
      enter: clearContextId,
      on: {
        [TooltipEvent.MOUSE_ENTER]: TooltipStates.FOCUSED,
        [TooltipEvent.FOCUS]: TooltipStates.VISIBLE,
      },
    },
    [TooltipStates.FOCUSED]: {
      enter: startRestTimer,
      leave: clearRestTimer,
      on: {
        [TooltipEvent.MOUSE_MOVE]: TooltipStates.FOCUSED,
        [TooltipEvent.MOUSE_LEAVE]: TooltipStates.IDLE,
        [TooltipEvent.MOUSE_DOWN]: TooltipStates.DISMISSED,
        [TooltipEvent.BLUR]: TooltipStates.IDLE,
        [TooltipEvent.REST]: TooltipStates.VISIBLE,
      },
    },
    [TooltipStates.VISIBLE]: {
      on: {
        [TooltipEvent.FOCUS]: TooltipStates.FOCUSED,
        [TooltipEvent.MOUSE_ENTER]: TooltipStates.FOCUSED,
        [TooltipEvent.MOUSE_LEAVE]: TooltipStates.LEAVING_VISIBLE,
        [TooltipEvent.BLUR]: TooltipStates.LEAVING_VISIBLE,
        [TooltipEvent.MOUSE_DOWN]: TooltipStates.DISMISSED,
        [TooltipEvent.SELECT_WITH_KEYBOARD]: TooltipStates.DISMISSED,
        [TooltipEvent.GLOBAL_MOUSE_MOVE]: TooltipStates.LEAVING_VISIBLE,
      },
    },
    [TooltipStates.LEAVING_VISIBLE]: {
      enter: startLeavingVisibleTimer,
      leave: () => {
        clearLeavingVisibleTimer();
        clearContextId();
      },
      on: {
        [TooltipEvent.MOUSE_ENTER]: TooltipStates.VISIBLE,
        [TooltipEvent.FOCUS]: TooltipStates.VISIBLE,
        [TooltipEvent.TIME_COMPLETE]: TooltipStates.IDLE,
      },
    },
    [TooltipStates.DISMISSED]: {
      leave: () => {
        // allows us to come on back later w/o entering something else first
        context.id = null;
      },
      on: {
        [TooltipEvent.MOUSE_LEAVE]: TooltipStates.IDLE,
        [TooltipEvent.BLUR]: TooltipStates.IDLE,
      },
    },
  },
};

export let state = chart.initial;

/**
 * We could require apps to render a <TooltipProvider> around the app and use
 * React context to notify Tooltips of changes to our state machine, instead
 * we manage subscriptions ourselves and simplify the Tooltip API.
 *
 * Maybe if default context could take a hook (instead of just a static value)
 * that was rendered at the root for us, that'd be cool! But it doesn't.
 */
type SubscribeFn = (state, context) => void;
const subscriptions: SubscribeFn[] = [];

export function subscribe(fn: SubscribeFn) {
  subscriptions.push(fn);
  return () => {
    subscriptions.splice(subscriptions.indexOf(fn), 1);
  };
}

function notify() {
  subscriptions.forEach((fn) => fn(state, context));
}

/**
 * Finds the next state from the current state + action. If the chart doesn't
 * describe that transition, it will throw.
 *
 * It also manages lifecycles of the machine, (enter/leave hooks on the state
 * chart)
 */
export const transition: Transition = (event, payload) => {
  const stateDef = chart.states[state];
  const nextState = stateDef && stateDef.on && stateDef.on[event];

  // Really useful for debugging
  // console.log({ event, state, nextState, contextId: context.id });
  // !nextState && console.log('no transition taken')

  if (!nextState) {
    return;
  }

  if (stateDef && stateDef.leave) {
    stateDef.leave();
  }

  if (payload) {
    context = payload;
  }

  const nextDef = chart.states[nextState];
  if (nextDef && nextDef.enter) {
    nextDef.enter();
  }

  state = nextState;
  notify();
};
